// src/pages/stylist/AddPortfolioItemPage.tsx
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { portfolioService } from '../../services/portfolioService';
import { useUI } from '../../context/UIContext';
import { usePauseIdleTimer } from '../../context/IdleLogoutContext';
import { 
  ArrowBackIcon, CameraIcon, CloseIcon
} from '../../components/common/Icons';
import TeslaButton from '../../components/common/TeslaButton';
import '../../styles/AddPortfolioItemPage.css';

const AddPortfolioItemPage: React.FC = () => {
  const navigate = useNavigate();
  const { setLoading: setUILoading, showSnackbar } = useUI();
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [caption, setCaption] = useState('');
  const [category, setCategory] = useState('');
  const [uploading, setUploading] = useState(false);

  // Pause the idle-logout timer while an upload is in progress.
  // Cleanup is enforced by usePauseIdleTimer's useEffect return — unmounting this
  // page (e.g. navigating away) always resumes the timer automatically.
  usePauseIdleTimer('portfolio-upload', uploading);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, isVideo: boolean = false) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    
    // Validate files
    for (const file of fileArray) {
      if (isVideo) {
        if (!file.type.startsWith('video/')) {
          showSnackbar('Please select a valid video file', 'error');
          return;
        }
        if (file.size > 100 * 1024 * 1024) { // 100MB limit for videos
          showSnackbar('Video file is too large. Maximum size is 100MB', 'error');
          return;
        }
      } else {
        if (!file.type.startsWith('image/')) {
          showSnackbar('Please select valid image files', 'error');
          return;
        }
        if (!portfolioService.validateImageFile(file)) {
          showSnackbar('Image is too large or invalid format. Maximum size is 5MB', 'error');
          return;
        }
      }
    }

    setSelectedFiles(fileArray);
    
    // Create previews for images only
    if (!isVideo) {
      const previewPromises = fileArray.map(file => {
        return new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.readAsDataURL(file);
        });
      });
      
      Promise.all(previewPromises).then(setPreviews);
    } else {
      setPreviews([]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      showSnackbar('Please select at least one file to upload', 'error');
      return;
    }

    try {
      setUploading(true);
      setUILoading(true);

      // Check if we have videos (only one video at a time)
      const videoFile = selectedFiles.find(f => f.type.startsWith('video/'));
      
      if (videoFile) {
        // Upload video
        const formData = new FormData();
        formData.append('video', videoFile);
        if (caption) formData.append('caption', caption);
        if (category) formData.append('category', category);
        
        await portfolioService.addPortfolioVideo(formData);
        showSnackbar('Video uploaded successfully', 'success');
      } else {
        // Upload images (multiple)
        const formData = new FormData();
        selectedFiles.forEach(file => {
          formData.append('image', file);
        });
        if (caption) formData.append('caption', caption);
        if (category) formData.append('category', category);
        
        await portfolioService.addPortfolioImage(formData);
        showSnackbar('Images uploaded successfully', 'success');
      }

      // Navigate back to portfolio page
      navigate('/stylist/portfolio');
    } catch (error: any) {
      console.error('Upload error:', error);
      if (error.response?.data?.error === 'File too large') {
        showSnackbar('File is too large. Please choose a smaller file.', 'error');
      } else {
        showSnackbar(error.response?.data?.error || 'Failed to upload files', 'error');
      }
    } finally {
      setUploading(false);
      setUILoading(false);
    }
  };

  return (
    <div className="add-portfolio-page">
      <div className="add-portfolio-header">
        <button className="add-portfolio-back" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </button>
        <h1 className="add-portfolio-title">Add Portfolio Items</h1>
      </div>

      <div className="add-portfolio-content">
        <div className="tesla-card">
          <div className="tesla-card__header">
            <h2 className="tesla-card__title">Upload Media</h2>
          </div>
          <div className="tesla-card__content">
            <div className="upload-section">
              <div className="upload-buttons">
                <button
                  className="tesla-button tesla-button--primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <CameraIcon />
                  Choose Photos
                </button>
                <button
                  className="tesla-button tesla-button--outline"
                  onClick={() => videoInputRef.current?.click()}
                  disabled={uploading}
                >
                  <CameraIcon />
                  Choose Video
                </button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleFileSelect(e, false)}
              />
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={(e) => handleFileSelect(e, true)}
              />

              {selectedFiles.length > 0 && (
                <div className="selected-files">
                  <h3 className="selected-files__title">
                    Selected Files ({selectedFiles.length})
                  </h3>
                  <div className="selected-files__grid">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="selected-file-item">
                        {previews[index] ? (
                          <img 
                            src={previews[index]} 
                            alt={file.name}
                            className="selected-file-preview"
                          />
                        ) : file.type.startsWith('video/') ? (
                          <div className="selected-file-preview selected-file-preview--video">
                            <span>Video</span>
                            <span className="selected-file-name">{file.name}</span>
                          </div>
                        ) : null}
                        <button
                          className="selected-file-remove"
                          onClick={() => handleRemoveFile(index)}
                        >
                          <CloseIcon />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="tesla-card">
          <div className="tesla-card__header">
            <h2 className="tesla-card__title">Details</h2>
          </div>
          <div className="tesla-card__content">
            <div className="form-group">
              <label htmlFor="caption">Caption (Optional)</label>
              <textarea
                id="caption"
                className="tesla-textarea"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Enter a caption for your portfolio item"
                rows={4}
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">Category (Optional)</label>
              <input
                type="text"
                id="category"
                className="tesla-input"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g., Haircuts, Coloring, Styling"
              />
            </div>
          </div>
        </div>

        <div className="add-portfolio-actions">
          <TeslaButton
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={uploading}
          >
            Cancel
          </TeslaButton>
          <TeslaButton
            variant="primary"
            onClick={handleUpload}
            loading={uploading}
            disabled={selectedFiles.length === 0}
          >
            Upload
          </TeslaButton>
        </div>
      </div>
    </div>
  );
};

export default AddPortfolioItemPage;

